// Shared @anter/ai-chat-sdk content, consumed by both the dedicated SDK page
// (/chat-sdk) and the Ask Anter use case (/resources/ask-anter). Keep this the
// single source of truth for install/config/snippets so the two pages never drift.

export const NPM_NAME = "@anter/ai-chat-sdk";
export const GITHUB_URL = "https://github.com/anter-ai/ai-chat-sdk";
// NOTE: anter-ai/anter-ai and anter-ai/agent-runtime are PRIVATE repos.
// Don't link to them from public pages — the link 404s for visitors and any
// "open source" framing around them is inaccurate. Only ai-chat-sdk is public.
export const BUILDER_GITHUB_URL = "https://github.com/anter-ai/anter-ai";
export const ORG_GITHUB_URL = "https://github.com/anter-ai";

// NOT YET ON npm. The registry name is reserved in intent only — `npm install
// @anter/ai-chat-sdk` 404s today, so these snippets install straight from the
// public GitHub repo instead. The repo's `prepare` script builds `dist/` on
// install (it is gitignored, so without `prepare` a git install would resolve
// every export path to a file that was never built).
// Swap both lines back to the registry form the day the package is published.
export const INSTALL_SNIPPET = `# Not published to npm yet — install from the public repo.
# The package builds itself on install.

# npm
npm install github:anter-ai/ai-chat-sdk

# pnpm
pnpm add github:anter-ai/ai-chat-sdk

# Pin to a commit for a reproducible install:
npm install github:anter-ai/ai-chat-sdk#<commit-sha>

# Load the widget styles once, at your app entry:
import "@anter/ai-chat-sdk/styles-no-base.css";`;

export const ADAPTER_SNIPPET = `import type { ChatAdapter, MessagePayload } from "@anter/ai-chat-sdk/types";

export class LandingChatAdapter implements ChatAdapter {
  constructor(private readonly agentId: string) {}

  private get streamPath(): string {
    return \`/api/v1/external/agent-runner/agents/\${this.agentId}/run-stream\`;
  }

  async sendMessage(payload: MessagePayload): Promise<ReadableStream<Uint8Array>> {
    const res = await fetch(this.streamPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: payload.message,
        organizationId: payload.organizationId,
        // contextVariables flow through to the agent. Pass per-session identity
        // so the runner can scope memory/history to this conversation.
        contextVariables: {
          ...(payload.contextVariables ?? {}),
          userId: \`chat-widget-\${payload.sessionId}\`,
          sessionId: payload.sessionId,
        },
      }),
    });
    if (!res.ok) throw new Error(\`sendMessage failed: \${res.status}\`);
    if (!res.body) throw new Error("sendMessage: missing response body");
    return res.body;
  }
}`;

export const PROVIDER_SNIPPET = `import { ChatProvider, ChatWidget } from "@anter/ai-chat-sdk";
import { LandingChatAdapter } from "./lib/landing-adapter";

const organizationId = (import.meta.env.VITE_ORG_ID as string | undefined) ?? "anter";
const chatAgentId = import.meta.env.VITE_CHAT_AGENT_ID as string;
const adapter = new LandingChatAdapter(chatAgentId);

<ChatProvider
  organizationId={organizationId}
  adapter={adapter}
  config={{
    theme: "light",
    enableModelSelector: false,
    enableSlashCommands: false,
    enableCommandPalette: false,
    enableFileUpload: false,
    enableSlashFocusShortcut: false,
    enableArtifacts: true,
  }}
  strings={{
    composerPlaceholder: "Ask a question...",
    footerDisclaimer: "AI responses can contain mistakes.",
  }}
>
  {page}
  <ChatWidget position="bottom-right" title="Anter" />
</ChatProvider>`;

export type ChatConfigOption = {
  option: string;
  landingValue: string;
  why: string;
  alternatives: string;
};

export const CHAT_CONFIG_OPTIONS: ChatConfigOption[] = [
  {
    option: "enableModelSelector",
    landingValue: "false",
    why: "Keep model choice controlled by backend/flow configuration.",
    alternatives: "Set `true` to expose model switching to end users.",
  },
  {
    option: "enableSlashCommands",
    landingValue: "false",
    why: "Simplify consumer-facing UX.",
    alternatives: "Set `true` for power-user workflows and command shortcuts.",
  },
  {
    option: "enableCommandPalette",
    landingValue: "false",
    why: "Avoid advanced command UI in public marketing chat.",
    alternatives: "Enable for internal apps or operator consoles.",
  },
  {
    option: "enableFileUpload",
    landingValue: "false",
    why: "Limit public-surface risk and keep scope to text chat.",
    alternatives: "Enable for document workflows once validation is in place.",
  },
  {
    option: "enableSlashFocusShortcut",
    landingValue: "false",
    why: "Prevent accidental shortcut conflicts on landing pages.",
    alternatives: "Enable for app-like experiences with keyboard-heavy users.",
  },
  {
    option: "enableArtifacts",
    landingValue: "true",
    why: "Allow rich response rendering with artifacts.",
    alternatives: "Disable if your use case requires strict plain-text output only.",
  },
];
